/**
 * Drop states — pure derivation.
 *
 * How a drop target should look while a card is in the air: the server's
 * grading → style + whether the drop is allowed / needs a confirm; the O(1)
 * index over a targets payload; and which 409 asks for which allow flag.
 * Nothing here touches React or the server.
 */
import { MoveTargetPhase, MoveTargets,MoveTargetWorker } from "../../../../../services/roster-assignments-api"; // eslint-disable-line sort-imports
import { lbb } from "./tokens";

export interface DropState {
	/** Outline colour while a card is in the air. */
	outline: string;
	fill: string;
	/** Short caption shown on the target ("2 of 2 already on it"); every graded state has one so colour is never the only signal. */
	caption: string | null;
	/** Whether the backend accepts the drop at all. */
	allowed: boolean;
	/** Whether the drop first asks "Place anyway?". */
	confirm: boolean;
}

/** A target the server did not grade: nothing known, let the server decide on drop. */
const NEUTRAL: DropState = { outline: lbb.navy20, fill: "transparent", caption: null, allowed: true, confirm: false };

/**
 * The server only lists targets that overlap the picked-up block's window. Once
 * the targets are known, a phase missing from them is one that runs at other
 * hours: the block cannot be moved there, but the person can still be placed
 * for the step's own hours by dragging their name from People.
 */
const NOT_RUNNING: DropState = {
	outline: lbb.navy20,
	fill: "transparent",
	caption: "runs at other hours · drag the name from People",
	allowed: false,
	confirm: false,
};

/**
 * A person missing from the targets is not on shift during the block. The
 * hand-off is still allowed: the server asks to confirm it as overtime.
 */
const NOT_ON_SHIFT: DropState = {
	outline: lbb.warning,
	fill: lbb.alloyWarm,
	caption: "off shift then · overtime",
	allowed: true,
	confirm: false,
};

/**
 * Week · by line: a card dropped on a phase in ANOTHER day's column. The
 * targets API only lists same-window steps, so such a phase is always absent
 * from them — but the refusal is decided by comparing days, never inferred
 * from the absence, so it names the day and not "other hours".
 */
export const DIFFERENT_DAY: DropState = {
	outline: lbb.navy20,
	fill: "transparent",
	caption: "different day · drag the name from People",
	allowed: false,
	confirm: false,
};

/** The drop state for a phase cell on `cellDay` while a card from `cardDay` (both yyyy-MM-dd) is in the air. */
export function dayPhaseDropState(
	cardDay: string | null | undefined,
	cellDay: string,
	target: MoveTargetPhase | undefined,
	targetsLoaded = false
): DropState {
	if (cardDay && cardDay !== cellDay) {
		return DIFFERENT_DAY;
	}
	return phaseDropState(target, targetsLoaded);
}

export function phaseDropState(target: MoveTargetPhase | undefined, targetsLoaded = false): DropState {
	if (!target) {
		return targetsLoaded ? NOT_RUNNING : NEUTRAL;
	}
	switch (target.state) {
		case "ok":
			return { outline: lbb.success, fill: lbb.successTint, caption: "can take it", allowed: true, confirm: false };
		case "full":
			return {
				outline: lbb.warning,
				fill: lbb.alloyWarm,
				caption:
					target.have != null && target.needed != null
						? `${target.have} of ${target.needed} already on it`
						: "already fully crewed",
				allowed: true,
				confirm: false,
			};
		case "unqualified":
			return {
				outline: lbb.warning,
				fill: lbb.alloyWarm,
				caption: target.reason || "not qualified",
				allowed: true,
				confirm: true,
			};
		case "current":
		default:
			return { ...NEUTRAL, allowed: false };
	}
}

/** Lower is better: a group of phases is graded by its most welcoming phase. */
const PHASE_RANK: Record<string, number> = { ok: 0, full: 1, unqualified: 2, current: 3 };

/**
 * The best grade among a crew group's phases, so a group that spans two
 * phases reads "can take it" when either of them can. Undefined when none of
 * the phases was graded.
 */
export function bestPhaseTarget(targets: Array<MoveTargetPhase | undefined>): MoveTargetPhase | undefined {
	return targets.reduce<MoveTargetPhase | undefined>((best, target) => {
		if (!target) {
			return best;
		}
		if (!best) {
			return target;
		}
		return (PHASE_RANK[target.state] ?? 9) < (PHASE_RANK[best.state] ?? 9) ? target : best;
	}, undefined);
}

export function workerDropState(target: MoveTargetWorker | undefined, targetsLoaded = false): DropState {
	if (!target) {
		return targetsLoaded ? NOT_ON_SHIFT : NEUTRAL;
	}
	switch (target.state) {
		case "free":
			return { outline: lbb.success, fill: lbb.successTint, caption: "can take it", allowed: true, confirm: false };
		case "busy":
			return {
				outline: lbb.brick,
				fill: lbb.brickTint,
				caption: target.reason || "already on something then",
				allowed: false,
				confirm: false,
			};
		case "unqualified":
			return {
				outline: lbb.warning,
				fill: lbb.alloyWarm,
				caption: target.reason || "not qualified",
				allowed: true,
				confirm: true,
			};
		case "current":
		default:
			return { ...NEUTRAL, allowed: false };
	}
}

/** Index a targets payload for O(1) lookups while dragging. */
export function indexTargets(targets: MoveTargets | undefined): {
	/** True once the server's targets for the picked-up block are known. */
	loaded: boolean;
	phase: (workOrderId: number, phaseId: number) => MoveTargetPhase | undefined;
	worker: (workerId: string) => MoveTargetWorker | undefined;
} {
	const phases = new Map((targets?.phases ?? []).map((p) => [`${p.workOrderId}:${p.phaseId}`, p]));
	const workers = new Map((targets?.workers ?? []).map((w) => [String(w.workerId), w]));
	return {
		loaded: targets !== undefined,
		phase: (workOrderId, phaseId) => phases.get(`${workOrderId}:${phaseId}`),
		worker: (workerId) => workers.get(String(workerId)),
	};
}

/** The machine-readable reason a 409 carries. */
export type ConflictCode = "UNQUALIFIED" | "OVERTIME" | "CLASH" | "NOT_RUNNING" | "NO_CREW_LINE";

export type ConfirmFlag = "allowUnqualified" | "allowOvertime";

/**
 * Which 409 asks for which allow flag; null = a hard block. The body's `code`
 * decides when it is there; the message wording is only the fallback for a
 * server that has not sent codes yet.
 */
export function confirmFlagFor(message: string, code?: ConflictCode | string | null): ConfirmFlag | null {
	if (code) {
		switch (String(code).toUpperCase()) {
			case "UNQUALIFIED":
				return "allowUnqualified";
			case "OVERTIME":
				return "allowOvertime";
			default:
				return null;
		}
	}
	const text = (message || "").toLowerCase();
	if (text.includes("confirm to place anyway")) {
		return "allowUnqualified";
	}
	if (text.includes("confirm to place as overtime")) {
		return "allowOvertime";
	}
	return null;
}
